/**
 * EventLogContext - State-Management für das Event-Log System
 *
 * Verwaltet die Event-Historie und stellt sie der gesamten App zur Verfügung.
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

// =============================================================================
// CONSTANTS
// =============================================================================

const MAX_EVENTS = 100; // Maximum Einträge im Log

// =============================================================================
// CONTEXT
// =============================================================================

const EventLogContext = createContext(null);

// =============================================================================
// PROVIDER
// =============================================================================

export function EventLogProvider({ children }) {
  const [events, setEvents] = useState([]);

  /**
   * Fügt ein neues Event hinzu (neueste oben)
   */
  const addEvent = useCallback((event) => {
    if (!event || !event.id) {
      console.warn('[EventLog] Event ohne ID ignoriert:', event);
      return;
    }

    setEvents(prev => {
      // Neuestes Event an den Anfang
      const newEvents = [event, ...prev];
      // Begrenze auf MAX_EVENTS
      return newEvents.slice(0, MAX_EVENTS);
    });
  }, []);

  /**
   * Fügt mehrere Events auf einmal hinzu
   */
  const addEvents = useCallback((newEvents) => {
    if (!Array.isArray(newEvents) || newEvents.length === 0) return;

    setEvents(prev => {
      // Neueste Events an den Anfang (in umgekehrter Reihenfolge, damit das neueste ganz oben ist)
      const validEvents = newEvents.filter(e => e && e.id);
      const combined = [...validEvents.reverse(), ...prev];
      return combined.slice(0, MAX_EVENTS);
    });
  }, []);

  /**
   * Löscht das gesamte Log (z.B. nach Partie-Ende)
   */
  const clearLog = useCallback(() => {
    setEvents([]);
  }, []);

  /**
   * Gibt das neueste Event zurück (für Mobile compact view)
   */
  const getLatestEvent = useCallback(() => {
    return events[0] || null;
  }, [events]);

  /**
   * Gibt Events eines bestimmten Typs zurück
   */
  const getEventsByType = useCallback((type) => {
    return events.filter(e => e.type === type);
  }, [events]);

  /**
   * Gibt Events eines bestimmten Spielers zurück
   */
  const getEventsByPlayer = useCallback((playerIndex) => {
    return events.filter(e => {
      if (e.type === 'group') {
        return e.mainEvent?.playerIndex === playerIndex;
      }
      return e.playerIndex === playerIndex;
    });
  }, [events]);

  // Memoized context value
  const value = useMemo(() => ({
    // State
    events,
    eventCount: events.length,

    // Actions
    addEvent,
    addEvents,
    clearLog,

    // Getters
    getLatestEvent,
    getEventsByType,
    getEventsByPlayer
  }), [events, addEvent, addEvents, clearLog, getLatestEvent, getEventsByType, getEventsByPlayer]);

  return (
    <EventLogContext.Provider value={value}>
      {children}
    </EventLogContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook zum Zugriff auf den EventLog Context
 *
 * @returns {Object} EventLog Context
 * @throws {Error} Wenn außerhalb des Providers verwendet
 */
export function useEventLog() {
  const context = useContext(EventLogContext);

  if (!context) {
    throw new Error('useEventLog must be used within an EventLogProvider');
  }

  return context;
}

/**
 * Hook nur für das neueste Event (für Mobile compact view)
 * Optimiert um unnötige Re-Renders zu vermeiden
 */
export function useLatestEvent() {
  const { events } = useEventLog();
  return events[0] || null;
}

/**
 * Hook für die Event-Anzahl (für Badges etc.)
 */
export function useEventCount() {
  const { eventCount } = useEventLog();
  return eventCount;
}

// =============================================================================
// EXPORTS
// =============================================================================

export default EventLogContext;
